import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogType?: 'website' | 'profile' | 'article';
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

const defaultSEO = {
  title: 'Full-Stack Developer & Business Solutions Architect | React',
  description: "Explore Ergys Onuzi's customizable portfolio—full-stack engineer delivering responsive UI, robust backends, and end-to-end projects to attract clients. Hire me.",
  keywords: 'web developer, full stack developer, PHP developer, MySQL, Next.js, React, AI development, business solutions, Magento developer, web applications, portfolio',
  ogImage: 'https://ergys.dev/og-image.png',
  siteUrl: 'https://ergys.dev'
};

export function SEO({
  title,
  description,
  keywords,
  ogType = 'website',
  ogImage,
  canonical,
  noindex = false,
}: SEOProps) {
  const pageTitle = title 
    ? `${title} | Ergys Onuzi` 
    : defaultSEO.title;
  
  const pageDescription = description || defaultSEO.description;
  const pageKeywords = keywords || defaultSEO.keywords;
  const pageImage = ogImage || defaultSEO.ogImage;
  const pageUrl = canonical 
    ? `${defaultSEO.siteUrl}${canonical}` 
    : defaultSEO.siteUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="Ergys Onuzi" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content="Ergys Onuzi Portfolio" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#0a0a0a" />
    </Helmet>
  );
}

// Structured Data Component for Person/Professional
export function StructuredData() {
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Ergys Onuzi",
    "url": "https://ergys.dev",
    "image": "https://ergys.dev/profile-image.jpg",
    "jobTitle": "Full Stack Developer & Business Solution Architect",
    "worksFor": {
      "@type": "Organization",
      "name": "Freelance / Independent"
    },
    "description": "Full Stack Developer specializing in PHP/MySQL, Next.js, React, and AI-assisted development",
    "knowsAbout": [
      "PHP",
      "MySQL",
      "Next.js",
      "React",
      "TypeScript",
      "JavaScript",
      "Tailwind CSS",
      "Node.js",
      "Magento",
      "AI Development",
      "Web Development",
      "Business Solutions"
    ],
    "sameAs": [
      "https://github.com/ergysonuzi",
      "https://linkedin.com/in/ergysonuzi"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Albania"
    },
    "email": "contact@ergys.dev"
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Ergys Onuzi Portfolio",
    "url": "https://ergys.dev",
    "description": "Professional portfolio showcasing web development projects and services",
    "author": {
      "@type": "Person",
      "name": "Ergys Onuzi"
    }
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Ergys Onuzi - Web Development Services",
    "url": "https://ergys.dev/services",
    "description": "Professional web development and business solution services",
    "serviceType": "Web Development",
    "providerMobility": "static",
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Web Development Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Custom Web Application Development",
            "description": "Full-stack web application development using modern technologies"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Business Solutions Architecture",
            "description": "Custom business solutions and system architecture design"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "E-commerce Development",
            "description": "Magento and custom e-commerce platform development"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(professionalServiceSchema)}
      </script>
    </Helmet>
  );
}